import { z } from "zod/v4";

import type { PassStatus } from "./pass-status";
import { parsePhone } from "./index";
import { deriveGuestPassStatus } from "./pass-status";

/** Canonical contact pair. `null` when neither phone nor email survives. */
export interface VisitorContact {
  phone: string | null;
  email: string | null;
}

const emailSchema = z.string().trim().toLowerCase().email().max(254);

/**
 * Single source of truth for visitor identity keys. Phone → E.164 via the
 * same `parsePhone` every write path uses; email → trimmed + lowercased.
 * An invalid part is dropped, not fatal — the other part may still identify.
 */
export function normalizeVisitorContact(input: {
  phone?: string | null;
  email?: string | null;
}): VisitorContact | null {
  const phone = input.phone ? parsePhone(input.phone) : null;
  const parsedEmail = input.email ? emailSchema.safeParse(input.email) : null;
  const email = parsedEmail?.success ? parsedEmail.data : null;
  if (!phone && !email) return null;
  return { phone: phone ?? null, email };
}

export interface VisitorIdentity {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  company: string | null;
}

export interface VisitorInput {
  name: string;
  phone: string | null;
  email: string | null;
  company: string | null;
}

export type VisitorMatchDecision =
  | { action: "create" }
  | {
      action: "link";
      id: string;
      /** Only fields the matched row lacks. Never `name`. */
      fill: Partial<Pick<VisitorIdentity, "phone" | "email" | "company">>;
      /** Set when phone and email resolved to two different rows. */
      conflictWith?: string;
    };

/**
 * The pure half of identity resolution: given the rows the DB found by phone
 * and by email, decide whether to link (and what to fill) or create.
 * Phone wins over email. Existing values are never overwritten.
 */
export function decideVisitorMatch(
  found: { byPhone: VisitorIdentity | null; byEmail: VisitorIdentity | null },
  input: VisitorInput,
): VisitorMatchDecision {
  const target = found.byPhone ?? found.byEmail;
  if (!target) return { action: "create" };
  const conflict =
    found.byPhone && found.byEmail && found.byPhone.id !== found.byEmail.id
      ? found.byEmail.id
      : undefined;
  const fill: Partial<Pick<VisitorIdentity, "phone" | "email" | "company">> = {};
  if (!target.phone && input.phone) fill.phone = input.phone;
  // Skip email when it's the very field that resolved to a *different* row
  // (byEmail) than the winning match (byPhone) — that email already belongs
  // to the conflicting row, so filling it onto `target` would misattribute it.
  if (!target.email && input.email && !conflict) fill.email = input.email;
  if (!target.company && input.company) fill.company = input.company;
  return conflict
    ? { action: "link", id: target.id, fill, conflictWith: conflict }
    : { action: "link", id: target.id, fill };
}

export const VISITOR_RETENTION_DEFAULT_MONTHS = 12;
export const VISITOR_RETENTION_MIN_MONTHS = 3;
export const VISITOR_RETENTION_MAX_MONTHS = 36;

export const visitorRetentionMonthsSchema = z
  .number()
  .int()
  .min(VISITOR_RETENTION_MIN_MONTHS)
  .max(VISITOR_RETENTION_MAX_MONTHS);

/** Required when blocking. Factual, short — it is shown to the gardien. */
export const blockedReasonSchema = z.string().trim().min(1).max(300);

export interface VisitorTags {
  vip: boolean;
  blocked: boolean;
  blockedReason: string | null;
}

/** « Arrivé » may only consume a pass that a scan would have allowed. */
export function confirmEligibility(
  status: PassStatus,
): { ok: true } | { ok: false; status: PassStatus } {
  return status === "active" ? { ok: true } : { ok: false, status };
}

/**
 * « Arrivé » eligibility for a guest (spec 2026-09-23 §8). An active pass
 * qualifies, as with `confirmEligibility`. So does an INCOMPLETE one — the
 * visitor never filled the form, and the agent checks ID at the desk instead —
 * but only when its window would make it active: the missing form is
 * forgiven, the day and hours are not. `pending` and `rejected` never qualify.
 */
export function canConfirmArrival(
  invite: Parameters<typeof deriveGuestPassStatus>[0],
  guest: { status: string; consumedAt: Date | null },
  now: Date,
  timeZone: string,
): { ok: true; incomplete: boolean } | { ok: false; status: PassStatus } {
  const status = deriveGuestPassStatus(invite, guest, now, timeZone);
  if (status === "active") return { ok: true, incomplete: false };
  if (status === "incomplete") {
    const asIfActive = deriveGuestPassStatus(
      invite,
      { status: "active", consumedAt: guest.consumedAt },
      now,
      timeZone,
    );
    if (asIfActive === "active") return { ok: true, incomplete: true };
  }
  return { ok: false, status };
}

export interface MineVisitorRow {
  visitor: VisitorIdentity;
  bookmarked: boolean;
  unitVisitCount: number;
  lastVisitAt: Date | null;
}

/** Favourites first (alphabetical), then the rest by most recent visit. */
export function sortMineVisitors<T extends MineVisitorRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.bookmarked !== b.bookmarked) return a.bookmarked ? -1 : 1;
    if (a.bookmarked) return a.visitor.name.localeCompare(b.visitor.name, "fr");
    const ta = a.lastVisitAt?.getTime() ?? 0;
    const tb = b.lastVisitAt?.getTime() ?? 0;
    return tb - ta;
  });
}
